<template>
  <div>
    <provisioning-status @refresh="load" />
    <v-card class="main-card">
      <v-card-title class="screen-title">
        <span>VNコネクト一覧</span>
        <manual-button path="/vn-list" />
      </v-card-title>
      <v-card-text class="pa-2" v-if="isLoaded">
        <v-row no-gutters>
          <v-col>
            <app-search :search="connect.search" v-model="connect.filter" />
          </v-col>
          <div class="d-flex flex-row ga-1 flex-wrap ml-2">
            <v-btn v-permission.admin color="primary" @click="limitModify"
              >上限値設定</v-btn
            >
            <v-btn
              v-permission.config
              v-if="contractSummary.isLargeScaleContract"
              color="primary"
              @click="lumpRegister"
              >一括設定</v-btn
            >
            <v-btn v-permission.config color="primary" @click="register"
              >追加</v-btn
            >
            <v-btn
              v-permission.config
              color="primary"
              @click="modifyStatus(true)"
              >有効</v-btn
            >
            <v-btn
              v-permission.config
              color="primary"
              @click="modifyStatus(false)"
              >無効</v-btn
            >
            <v-btn
              color="primary"
              :href="$api.vNConnect.getVnconnectCsvUrl()"
              download
              >一覧ダウンロード</v-btn
            >
          </div>
        </v-row>
        <v-alert v-if="alertMsg" class="my-2" :text="alertMsg"></v-alert>
        <div class="d-flex flex-row justify-end mb-2">
          <v-btn color="primary" @click="load">一覧更新</v-btn>
        </div>
        <app-table
          v-model:table-sort-by="connect.sortBy"
          primary-key="wnumber"
          :selectable="selectable"
          :fields="connect.fields"
          :items="connect.items"
          :filterOption="connect.filter_option"
          :filter="connect.filter"
          v-model="connect.selected"
          :hover="true"
          sticky-header="50vh"
          @click:row="connectDetails"
        >
          <!-- 選択チェックボックス（PFGW-インターネットは有効/無効不可、V連携は有効/無効不可） -->
          <template #cell(_selected)="data">
            <v-checkbox-btn
              v-if="data.item.enumber && !data.item.isAssociate"
              :value="data.item"
              v-model="connect.selected"
              @click.stop
            />
          </template>
          <!-- 回線番号（E番号） -->
          <template #cell(enumber)="data">
            {{ $filter.emptyTo(data.item.enumber, "-") }}
          </template>
          <!-- サイト名 -->
          <template #cell(siteName)="{ value }">
            {{ $filter.emptyTo(value, "-") }}
          </template>
          <!-- IF状態 -->
          <template #cell(ifStatus)="data">
            {{ $filter.enumTo(data.item.ifStatus, "cloudVn_ifStatus") }}
          </template>
          <!-- 有効/無効 -->
          <template #cell(isVnConnectStatus)="data">
            {{ $filter.enumTo(data.item.isVnConnectStatus, "enable") }}
          </template>
        </app-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { VNConnect } from "@/apis/VNConnectApi";
import { mapState } from "vuex";
import VNConnectReference from "@/modals/vnConnect/VNConnectReference.vue";
import VNConnectStatusModify from "@/modals/vnConnect/VNConnectStatusModify.vue";
import type {
  AppTableHeader,
  FilterOption,
  SortItem,
} from "@/components/AppTable.vue";
import type { SearchOption } from "@/components/AppSearch.vue";
import LimitModify from "@/modals/common/LimitModify.vue";
import VNConnectEdit from "@/modals/vnConnect/VNConnectEdit.vue";
import VNConnectLumpRegister from "@/modals/vnConnect/VNConnectLumpRegister.vue";

export default defineComponent({
  name: "VNConnectList",
  data() {
    return {
      alertMsg: null as string | null,
      statusMixed: false,
      connect: {
        sortBy: { key: "wnumber", order: "desc" } as SortItem,
        fields: [
          {
            key: "wnumber",
            width: "200px",
            title: "VNコネクト番号",
            sortable: true,
          },
          {
            key: "vnConnectName",
            width: "200px",
            title: "VNコネクト名",
            sortable: true,
          },
          {
            key: "enumber",
            width: "250px",
            title: "回線番号(E番号)",
            sortable: true,
          },
          {
            key: "vlan",
            width: "170px",
            title: "VLAN",
            sortable: true,
            align: "end",
            sortByFormatted: true,
            filterByFormatted: true,
            formatter: (value: string, key: null, item: VNConnect) => {
              if (item.vlanIdType === "SPECIFIED") {
                return `${item.vlanId}`;
              } else {
                return this.$filter.enumTo(item.vlanIdType, "vlanIdType");
              }
            },
          },
          {
            key: "dstVpnVn",
            width: "250px",
            title: "接続先VPN/VNコード",
            sortable: true,
            sortByFormatted: true,
            filterByFormatted: true,
            formatter: (value: string, key: null, item: VNConnect) => {
              if (item.vnName) {
                return `${item.vnName}(${item.vpnVnCode}:${item.vnType})`;
              } else {
                return `${item.vpnVnCode}`;
              }
            },
          },
          {
            key: "siteName",
            width: "170px",
            title: "サイト名",
            sortable: true,
          },
          {
            key: "ifStatus",
            width: "170px",
            title: "IF状態",
            sortable: true,
          },
          {
            key: "isVnConnectStatus",
            width: "170px",
            title: "有効/無効",
            sortable: true,
          },
        ] as AppTableHeader<VNConnect>[],
        items: [] as VNConnect[],
        selected: [] as VNConnect[],
        search: {
          wnumber: { type: "text", label: "VNコネクト番号" },
          vnConnectName: { type: "text", label: "VNコネクト名" },
          enumber: { type: "text", label: "回線番号(E番号)" },
          vlan: { type: "text", label: "VLAN" },
          dstVpnVn: { type: "text", label: "接続先VPN/VNコード" },
          siteName: { type: "text", label: "サイト名" },
          ifStatus: {
            type: "select",
            label: "IF状態",
            items: [
              { value: "NO_SHUTDOWN", text: "no shutdown" },
              { value: "SHUTDOWN", text: "shutdown" },
              { value: null, text: "-" },
            ],
          },
          isVnConnectStatus: {
            type: "select",
            label: "有効/無効",
            items: [
              { value: true, text: "有効" },
              { value: false, text: "無効" },
            ],
          },
        } as SearchOption,
        filter: {} as Record<string, unknown>,
        filter_option: {
          wnumber: "like",
          vnConnectName: "like",
          enumber: "like",
          vlan: "like",
          dstVpnVn: "like",
          siteName: "like",
          ifStatus: "equal",
          isVnConnectStatus: "equal",
        } as FilterOption,
      },
      keyId: "",
      isLoaded: false,
    };
  },
  computed: {
    ...mapState("user", {
      contractSummary: "contractSummary",
    }),
    /** true: 一覧選択可能, false: 不可 */
    selectable(): boolean {
      return this.$service.permission.hasAuthority("CONFIG");
    },
  },
  async mounted() {
    await this.load();
    this.isLoaded = true;
  },
  methods: {
    async load() {
      this.alertMsg = null;
      /** VNコネクトを一覧取得APIの呼び出し処理 */
      const connectsData = await this.$api.vNConnect.getVNConnects();
      this.connect.items = connectsData.vnConnectList;
      this.keyId = connectsData.vnConnectKeyId;
    },

    /** 上限値設定 */
    async limitModify() {
      this.alertMsg = null;
      await this.$modal.show(LimitModify, {
        settingType: "vnConnect",
      });
      await this.load();
    },

    /** 一括設定 */
    async lumpRegister() {
      this.alertMsg = null;
      await this.$modal.show(VNConnectLumpRegister);
      await this.load();
    },

    /** 追加 */
    async register() {
      this.alertMsg = null;
      await this.$modal.show(VNConnectEdit, {
        keyId: this.keyId,
        vnConnects: this.connect.items,
      });
      await this.load();
    },

    /** 有効・無効 */
    async modifyStatus(status: boolean) {
      if (this.connect.selected.length === 0) {
        this.alertMsg = this.$filter.message("no_select_data");
        return;
      }
      if (this.connect.selected.length > 10) {
        this.alertMsg = this.$filter.message("vn_connect_limit");
        return;
      }
      // 既に指定した状態になっているものがないかチェック
      if (status) {
        if (this.connect.selected.find((e) => e.isVnConnectStatus)) {
          this.alertMsg = this.$filter.message("vn_connect_activate");
          return;
        }
      } else {
        if (this.connect.selected.find((e) => !e.isVnConnectStatus)) {
          this.alertMsg = this.$filter.message("vn_connect_deactivate");
          return;
        }
      }

      this.alertMsg = null;
      await this.$modal.show(VNConnectStatusModify, {
        wnumbers: this.connect.selected.map((e) => e.wnumber),
        status: status ? "有効" : "無効",
      });

      if (status) {
        await this.$api.vNConnect.postVNConnectEnable({
          orderList: this.connect.selected.map((e) => e.wnumber),
          vnConnectKeyId: this.keyId,
        });
      } else {
        await this.$api.vNConnect.postVNConnectDisable({
          orderList: this.connect.selected.map((e) => e.wnumber),
          vnConnectKeyId: this.keyId,
        });
      }

      await this.load();
    },

    /** VNコネクト詳細表示 */
    async connectDetails(item: VNConnect) {
      this.alertMsg = null;
      await this.$modal.show(VNConnectReference, {
        wnumber: item.wnumber,
      });
      await this.load();
    },
  },
});
</script>
